import { Component, Input, ViewChild } from '@angular/core';
import {
  ToastController,
  Content,
  NavController,
  AlertController,
} from 'ionic-angular';
import * as debug from 'debug';
import * as _ from 'lodash';
import * as moment from 'moment';
import { CrudApiProvider } from '../../providers/crud-api/crud-api';
import { ToolsProvider } from '../../providers/tools/tools';
import { UserProvider } from '../../providers/user/user';
import { ConfigProvider } from '../../providers/config/config';
import { UiToolsProvider } from '../../providers/ui-tools/ui-tools';
import { EventEmitter as EventEmitter2 } from '../../includes/eventEmitter';
import { ContentToolsProvider } from '../../providers/content-tools/content-tools';

declare let CmKit: any;

@Component({
  selector: 'comment',
  templateUrl: 'comment.html',
})
export class CommentComponent {
  log = debug('app:CommentComponent');

  @Input()
  addCommentComponent: EventEmitter2;
  @Input()
  content: Content;
  @Input()
  comment: any = {};
  @Input()
  blockOpenPost: any = false;
  @Input()
  reply: any = false;
  @Input()
  historyPage = false;
  @Input()
  scrollTo = false;
  @Input() highlightEngagement = null;
  @Input()
  hideFooter = false;
  @Input()
  flaggedContent = false;
  @Input()
  hideFlaggedRed = false;
  @Input()
  lastChild = false;
  @Input()
  post: any = {};
  @Input()
  thread;

  @Input() lazyLoad = true;
  @ViewChild('commentContainer')
  commentContainer;
  @ViewChild('repliesContainer')
  repliesContainer;
  @ViewChild('replyTextarea')
  replyTextarea;

  liking = false;
  closeToVisible = false;
  commentDisplayThreshold = 0;
  showReplyInput = false;
  userEngagements: any = {};
  replyLimiter = 2;
  smallScreen = false;
  currentReplyListener;
  newReply = {
    commentInformation: {
      text: '',
    },
    engagementType: 'COMMENT',
    stats: {
      comments: 0,
      shares: 0,
      likes: 0,
    },
  };
  oldCommentText = '';
  editingComment = false;
  visibilitySetting = 'SHOW';

  subs: any = [];
  rightIcon = '';
  metadataId;
  parsedText = '';
  linkInformation = {
    linkUrl: '',
    ampUrl: '',
    image: '',
    description: '',
    title: '',
  };

  commentTags = [];

  impression = false;

  constructor(
    public crudApi: CrudApiProvider,
    public tools: ToolsProvider,
    public toastCtrl: ToastController,
    public alertCtrl: AlertController,
    public user: UserProvider,
    public config: ConfigProvider,
    public navCtrl: NavController,
    public uiTools: UiToolsProvider,
    public contentTools: ContentToolsProvider
  ) {
    this.log('constructed');
  }

  async ngOnInit() {
    this.log('comment', this.comment);
    const engagements = _.get(this.comment, 'userEngagements');
    this.userEngagements = this.tools.userEngagementsOnPost(engagements);
    this.commentDisplayThreshold = this.config.get('commentDisplayThreshold');

    this.visibilitySetting = this.contentTools.getVisibilitySettingForContent(
      this.comment
    );

    // Let's see if there are any links in the comment text
    if (_.get(this.comment, 'commentInformation.text')) {
      this.parsedText = this.replaceUrls(this.comment.commentInformation.text);
      this.replaceTags(this.parsedText);
    }

    // If I should see it, let's just override
    if (this.user.userShouldSeeContent(this.comment)) {
      _.set(this.comment, 'states.isBanned', false);
    } else {
      _.set(this.comment, 'states.isBanned', true);
    }

    this.subs.push(
      this.user.on('hideShowContent', () => {
        if (!this.user.userShouldSeeContent(this.post)) {
          _.set(this.comment, 'states.isBanned', true);
        }
      })
    );

    this.log('commentDisplayThreshold', this.commentDisplayThreshold);

    // for styling screens smaller than iphone6-7-8 (375px)
    if (this.content && _.get(this.content, 'contentWidth') <= 359) {
      this.log('smaller screen');
      this.smallScreen = true;
    }

    this.setSubscriptions();

    if (this.highlightEngagement) {
      setTimeout(() => {
        this.highlightEngagement = null;
      }, 3000);
    }

    if (this.scrollTo) {
      setTimeout(() => {
        if (this.content) {
          this.content.scrollTo(
            0,
            this.repliesContainer.nativeElement.offsetTop - 150
          );
        }

        setTimeout(() => {
          this.showMore();
        }, 200);
      }, 50);
    }
  }

  ngOnDestroy() {
    this.clearSubscriptions();
  }

  trackAnalytics() {
    if (!this.impression) {
      this.tools.logAnalyticsEvent('trackCustom', 'comment-impression');
    }
    this.impression = true;
  }

  private setSubscriptions() {
    this.clearSubscriptions();
    this.replyListener();

    this.log('setting subscriptions', _.get(this.comment, '_id'));

    this.subs.push(
      this.addCommentComponent.on('updateReplyListener', () => {
        this.setSubscriptions();
      })
    );
    this.subs.push(
      this.addCommentComponent.on('updateUpdateListener', () => {
        this.updateListener();
      })
    );
    this.subs.push(
      this.tools.on(`post-show-sensitive-clicked-${this.comment.post}`, () => {
        _.set(this.comment, 'states.isSensitive', false);
        this.visibilitySetting = 'SHOW';
      })
    );

    this.updateListener();

    this.subs.push(
      this.addCommentComponent.on('clearingCommentInput', () => {
        if (_.get(this, 'oldCommentText')) {
          _.set(
            this.comment,
            'commentInformation.text',
            _.get(this, 'oldCommentText')
          );
        }
        this.editingComment = false;
      })
    );

    this.setRightIcon();
  }

  showSensitiveClicked() {
    this.comment.states.isSensitive = false;
    this.visibilitySetting = 'SHOW';
    this.tools.emit(`post-show-sensitive-clicked-${this.comment.post}`);
    this.tools.logAnalyticsEvent('trackCustom', 'show-sensitive-comment', {
      comment: _.get(this, 'comment._id'),
    });
  }

  openedPhoto() {
    this.tools.logAnalyticsEvent('trackCustom', 'comment-open-photo', {
      comment: _.get(this, 'comment._id'),
    });
  }

  openedVideo() {
    this.tools.logAnalyticsEvent('trackCustom', 'comment-open-video', {
      comment: _.get(this, 'comment._id'),
    });
  }

  private clearSubscriptions() {
    _.each(this.subs, s => {
      if (s instanceof Function) {
        s();
      } else if (s.unsubscribe) {
        s.unsubscribe();
      }
    });
    this.subs = [];
  }

  private updateListener() {
    this.log('anchor listener update');
    this.addCommentComponent.on(
      `updateComment-${_.get(this.comment, '_id')}`,
      c => {
        this.log('updating comment', c._id);
        this.log('updating comment', c);

        this.crudApi
          .put(`/api/engagements/${_.get(c, '_id')}`, {
            taggedUsers: _.get(c, 'taggedUsers'),
            commentInformation: {
              text: _.get(c.commentInformation, 'text'),
              giphyUrl: _.get(c.commentInformation, 'giphyUrl'),
              imageUrl: _.get(c.commentInformation, 'imageUrl'),
              videoUrl: _.get(c.commentInformation, 'videoUrl'),
            },
          })
          .then(() => {
            _.set(
              this.comment.commentInformation,
              'text',
              _.get(c.commentInformation, 'text')
            );

            if (_.get(c.commentInformation, 'text')) {
              this.linkInformation = {
                linkUrl: '',
                ampUrl: '',
                image: '',
                description: '',
                title: '',
              };
              this.parsedText = this.replaceUrls(
                _.get(c.commentInformation, 'text')
              );
              this.replaceTags(this.parsedText);
            }

            _.set(this.comment, 'taggedUsers', _.get(c, 'taggedUsers'));

            if (_.get(c.commentInformation, 'giphyUrl')) {
              setTimeout(() => {
                _.set(
                  this.comment.commentInformation,
                  'giphyUrl',
                  _.get(c.commentInformation, 'giphyUrl')
                );
              }, 100);
            } else if (!_.get(c.commentInformation, 'giphyUrl')) {
              setTimeout(() => {
                _.set(this.comment.commentInformation, 'giphyUrl', null);
              }, 100);
            }
            if (_.get(c.commentInformation, 'imageUrl')) {
              setTimeout(() => {
                _.set(
                  this.comment.commentInformation,
                  'imageUrl',
                  _.get(c.commentInformation, 'imageUrl')
                );
              }, 100);
            } else if (!_.get(c.commentInformation, 'imageUrl')) {
              setTimeout(() => {
                _.set(this.comment.commentInformation, 'imageUrl', null);
              }, 100);
            }
            if (_.get(c.commentInformation, 'videoUrl')) {
              setTimeout(() => {
                _.set(
                  this.comment.commentInformation,
                  'videoUrl',
                  _.get(c.commentInformation, 'videoUrl')
                );
              }, 100);
            } else if (!_.get(c.commentInformation, 'videoUrl')) {
              setTimeout(() => {
                _.set(this.comment.commentInformation, 'videoUrl', null);
              }, 100);
            }
            this.editingComment = false;
          })
          .catch(e => {
            this.log('there was an error updating the comment', e);
            _.set(
              this.comment,
              'commentInformation.text',
              _.get(this, 'oldCommentText')
            );
            this.editingComment = false;
          });
      }
    );
  }

  private setRightIcon() {
    // Should users be able to delete their own comments?
    // What if they have been replied to?
    this.rightIcon = '';
    const userId = _.get(this, 'user.info._id'),
      commentUserId = _.get(this, 'comment.user._id'),
      postType = _.get(this, 'post.postType'),
      postUserId = _.get(this, 'post.user');

    this.log(`userId: ${userId}, commentUserId: ${commentUserId}`);
    if (!this.hideFooter) {
      if (userId !== commentUserId) {
        this.rightIcon = 'flag';
      } else {
        this.rightIcon = 'edit';
      }
    }
    // is user moderator and post type is Advertisement
    if (this.user.isModerator()) {
      // user should be able to delete the comment or flag the comment
      // delete, flag
      this.rightIcon = 'ellipsis';
    }

    // is user is owner of the post
    if (postUserId === userId) {
      // user should be able to both delete the comment and flag the comment
      // delete, flag
      this.rightIcon = 'ellipsis';
    }
  }

  async presentEllipsisActionSheet() {
    await this.contentTools.actionSheet(this.comment, 'engagements', {
      navCtrl: this.navCtrl,
      userEngagements: {},
      isParentOwner: this.user.get('_id') === _.get(this.post, 'user._id'),

      // Fns ----
      onEdit: () => {
        this.editComment();
      },
      onDelete: () => {},
    });
  }

  private replyListener() {
    this.log('subscriptions here', this.subs);
    let replyId = 'addReply-' + _.get(this.comment, '_id');

    this.log('ADDCOMMENT parentReply', this.comment);
    this.log(
      'ADDCOMMENT - emitter id',
      replyId,
      this.comment.commentInformation.text
    );

    // unsubscribe old one
    if (this.currentReplyListener) {
      this.currentReplyListener();
    }

    this.subs.push(
      (this.currentReplyListener = this.addCommentComponent.on(
        `${replyId}`,
        newReply => {
          _.set(this, 'newReply', {
            post: '',
            commentInformation: {
              text: '',
            },
            engagementType: 'COMMENT',
            stats: {
              comments: 0,
              shares: 0,
              likes: 0,
            },
          });
          this.log('ADDCOMMENT - ADDING REPLY', _.clone(newReply));
          this.addReply(newReply);
        }
      ))
    );
  }

  private editComment() {
    this.log('editing comment', this.comment);
    let comment = _.cloneDeep(this.comment);
    _.set(
      this,
      'oldCommentText',
      _.get(this.comment.commentInformation, 'text')
    );
    _.set(this.comment.commentInformation, 'text', '');
    this.log('editing comment', comment);
    this.editingComment = true;
    this.addCommentComponent.emit('editComment', comment);
    this.tools.logAnalyticsEvent('trackCustom', 'comment-edit', {});
  }

  showMore() {
    this.replyLimiter += 10;
  }

  hideReplies() {
    setTimeout(() => {
      this.replyLimiter = 2;
    }, 100);
  }

  scrollToComments() {
    setTimeout(() => {
      if (this.content) {
        this.content.scrollTo(0, this.commentContainer.nativeElement.offsetTop);
      }
    }, 200);
  }

  private addReply(reply) {
    let engagement = {
      post: _.get(reply, 'post'),
      parentComment: _.get(reply, 'parentComment') || null,
      parentReply: _.get(reply, 'parentReply') || null,
      parentReplyUser: _.get(reply, 'parentReplyUser') || null,
      parentReplyObjTmp: _.get(reply, 'parentReplyObjTmp') || null,
      engagementType: _.get(reply, 'engagementType') || null,
      taggedUsers: _.get(reply, 'taggedUsers') || null,
      commentInformation: {
        text: _.get(reply, 'commentInformation.text') || null,
        giphyUrl: _.get(reply, 'commentInformation.giphyUrl') || null,
        imageUrl: _.get(reply, 'commentInformation.imageUrl') || null,
        videoUrl: _.get(reply, 'commentInformation.videoUrl') || null,
      },
    };
    this.log('ADDCOMMENT - ADDING REPLY HERE', engagement);

    let c: any = engagement;
    // need for immediate display of user date info
    // overwritten in api
    c.user = this.user.get();
    c.dates = {
      created: moment().toDate(),
    };
    this.userEngagements = this.tools.userEngagementsOnPost(
      this.userEngagements
    );
    if (_.size(_.get(c, 'parentReply'))) {
      c.parentReplyUser = _.get(c, 'parentReplyObjTmp.user._id');
    }
    if (this.replyLimiter === 2) this.replyLimiter = 10;
    _.set(c, 'score.numerator', 999); // needed for showHide comments on('clearingCommentInput')
    try {
      this.comment.replies.push(c);

      this.tools.logEventAndFlush('reply');
    } catch (error) {
      this.log(`engagement post error: ${error} `, [c]);
      this.comment.replies = [c];
      this.tools.logAnalyticsEvent(
        'trackCustom',
        'engagement-comment-reply-error',
        {}
      );
    }
    this.log('ADDCOMMENT full comment structure: ', this.comment);

    setTimeout(() => {
      try {
        if (this.content) {
          this.content.scrollTo(
            0,
            this.repliesContainer.nativeElement.offsetTop
          );
        }
      } catch (e) {}
    }, 200);

    try {
      this.comment.stats.comments += 1;
    } catch (e) {
      this.log('counldnt add stats', this.comment);
      this.tools.logAnalyticsEvent('trackCustom', 'comment-stats-error', {});
    }
    this.newReply.commentInformation.text = '';
    this.showReplyInput = false;
    this.log(' ADDCOMMENT userTag? ', _.cloneDeep(c));
    let tmpParentReplyObjTmp = c.parentReplyObjTmp;
    c.parentReplyObjTmp = null;
    c.stats = c.stats || {
      likes: 0,
      comments: 0,
      reports: 0,
      shares: 0,
    };

    const commentPayload = _.cloneDeep(c);
    _.set(commentPayload, 'user', _.get(commentPayload, 'user._id'));

    this.crudApi.post('/api/engagements', commentPayload).then(
      res => {
        this.log('ADDCOMMENT userTag? SUCCESS', res.data);
        _.set(engagement, '_id', _.get(res.data, '_id'));
        c.parentReplyObjTmp = tmpParentReplyObjTmp;
        this.addCommentComponent.emit('updateReplyListener');
        this.addCommentComponent.emit(
          'updateUpdateListener',
          _.get(engagement, '_id')
        );
        this.addCommentComponent.emit('addCommentSuccess');
      },
      e => {
        this.log('ADDCOMMENT userTag? ERROR', e);
        this.addCommentComponent.emit('addCommentError', engagement);
        try {
          this.comment.stats.comments -= 1;
        } catch (e) {
          this.log('counldnt add stats', this.comment);
          this.tools.logAnalyticsEvent(
            'trackCustom',
            'comment-stats-error',
            {}
          );
        }
        _.remove(this.comment.replies, c);

        const toast = this.toastCtrl.create({
          message:
            'An error occurred while replying to a comment. Please check your connection and try again. If the issue continues, please contact us for help.',
          position: 'top',
          duration: 5000,
        });
        toast.present();
      }
    );

    // done after to not send to API
    if (c.parentReply) {
      c.parentReply = _.get(reply, 'parentReplyObjTmp') || this.comment;
    }
  }

  likeComment() {
    if (this.liking) {
      return;
    }
    this.liking = true;

    if (!this.user.userPermission('like')) {
      this.liking = false;
      return this.tools.loginBlock('You must be logged in to like a post.');
    }

    this.log('liking', this.comment);

    if (_.get(this.userEngagements, 'liked')) {
      this.deleteEngagement('LIKE', 1);
      this.userEngagements.liked = false;
      this.liking = false;
      return;
    }
    if (_.get(this.userEngagements, 'disliked')) {
      this.deleteEngagement('LIKE', -1);
      this.userEngagements.liked = false;
      this.userEngagements.disliked = false;
      this.liking = false;
    }
    this.comment.stats.likes += 1;
    this.userEngagements.liked = true;

    this.crudApi
      .post('/api/engagements', {
        post: _.get(this.comment, 'post'),
        parentComment: _.get(this.comment, '_id'),
        engagementType: 'LIKE',
        likeInformation: { value: 1 },
      })
      .then(response => {
        this.log('posted Like', response.data, this.comment);

        try {
          this.comment.userEngagements.push(response.data);
        } catch (e) {
          this.comment['userEngagements'] = [];
          this.comment.userEngagements.push(response.data);
        }

        this.tools.logEventAndFlush('upVote');
        this.liking = false;
      })
      .catch(e => {
        this.log('an error occured', e);
        this.comment.stats.likes -= 1;
        this.tools.logAnalyticsEvent(
          'trackCustom',
          'engagement-comment-like-error',
          {}
        );
      });
  }

  private deleteEngagement(engagementType, likeValue?) {
    this.log('deleteEngagement', this.comment);

    const engagement = _.find(this.comment.userEngagements, d => {
      if (engagementType === 'LIKE') {
        this.log('like/unlike', d);
        return (
          d.engagementType === engagementType &&
          likeValue === _.get(d, 'likeInformation.value')
        );
      }
      return d.engagementType === engagementType;
    });

    if (!engagement) {
      this.log('engagement not found');
      return;
    }
    this.log('engagement found', engagement._id);
    this.log('local engagements b4', this.comment.userEngagements);
    this.log('deleteEngagement found');
    // Remove from user engagement list locally
    this.comment.userEngagements = _.filter(this.comment.userEngagements, e => {
      return e._id !== engagement._id;
    });

    this.log('local engagements after', this.userEngagements);

    // TODO: update stats
    if (engagementType === 'LIKE' && likeValue === 1) {
      this.comment.stats.likes -= 1;
    } else if (engagementType === 'LIKE' && likeValue === -1) {
      this.comment.stats.dislikes -= 1;
    }

    // update booleans
    this.log('local userengagemnts b4', this.userEngagements);
    this.log('local userengagemnts after', this.userEngagements);
    this.crudApi.delete(`/api/engagements/${engagement._id}`).then(
      response => {
        this.log('Delete Response: ', response);
      },
      e => {
        this.tools.defaultAlert('An error occured.');
        this.tools.logAnalyticsEvent(
          'trackCustom',
          'comment-engagment-delete-error',
          {}
        );
      }
    );
  }

  blockOpen() {
    const id = (this.blockOpenPost = _.uniqueId());
    setTimeout(() => {
      // make sure blockopen wasn't called again
      if (this.blockOpenPost === id) {
        this.blockOpenPost = false;
      }
    }, 500);
    return id;
  }

  navToUsersProfile(u?) {
    u = u || _.get(this, 'comment.user');
    this.navCtrl.push('UserProfilePage', {
      user: u,
      user_id: _.get(u, '_id'),
    });
  }

  focusReply() {
    this.log('addReply: ', this.comment);
    this.addCommentComponent.emit('focusAddReply', this.comment);
  }

  private async replaceTags(text: string) {
    let postWords = this.tools.taggingWords(text);
    let links = [];
    _.each(postWords, (word, i) => {
      const newword = `link:${word}`;
      if (
        _.includes(
          _.map(this.comment.taggedUsers, u => `@${u.username}`),
          word
        ) &&
        !_.includes(links, newword)
      ) {
        postWords[i] = newword;
        links.push(newword);
      }
    });
    text = _.join(postWords, ' ');
    this.parsedText = text;
    const userAnchorRegex = /link:@(\S+)/gi;
    let userAnchors = _.words(text, userAnchorRegex);
    if (_.size(userAnchors)) {
      let promises = [];
      userAnchors.forEach(userAnchor => {
        this.log('anchor', userAnchor);
        // this.commentTags.push(`cdlc-user-anchor-comment-${this.comment._id}-${user._id}`);
        promises.push(this.replaceTagAnchors(userAnchor, this.parsedText));
      });

      for (let promise of promises) {
        await promise;
      }

      setTimeout(() => {
        document
          .querySelectorAll(`.cdlc-user-anchor-comment-${this.comment._id}`)
          .forEach(anchor => {
            anchor.addEventListener('click', () => {
              const userId = _.replace(
                _.clone(anchor.classList[2]),
                'userId-',
                ''
              );
              this.navCtrl.push('UserProfilePage', {
                user_id: userId,
              });
            });
          });
      }, 250);
    } else {
      this.parsedText = text;
    }
  }

  async replaceTagAnchors(link, textContent) {
    const joinTextRegex = /[^ ]+/g;
    const username = _.split(link, 'link:@')[1];
    // const urlAnchor = document.createElement('div');
    // const text = document.createTextNode(_.replace(link, 'link:', ''));
    const user = _.find(this.comment.taggedUsers, u => u.username === username);

    // not sure why this didn't work, but setAttribute doesn't populate
    // urlAnchor.setAttribute('id', user._id);
    // urlAnchor.classList.add(`cdlc-user-anchor-comment`);
    // urlAnchor.classList.add(`userId-${user._id}`);
    // urlAnchor.appendChild(text);

    if (_.get(user, '_id')) {
      const anchor = `<div class="cdlc-user-anchor-comment cdlc-user-anchor-comment-${
        this.comment._id
      } userId-${user._id}" id="${user._id}">${_.replace(
        link,
        'link:',
        ''
      )}</div>`;

      // let tempStr = textContent;
      textContent = _.replace(textContent, link, anchor);
    }

    this.parsedText = textContent;
  }

  private replaceUrls(text: string) {
    let urls = this.tools.urlInString(text, true);
    const urlRegex = new RegExp('((http|https)(://)?)');
    if (!urls || !urls.length) return text;

    let str = text;
    for (let i = 0; i < urls.length; i++) {
      const id = (this.metadataId = _.uniqueId('metadata-'));

      try {
        if (this.metadataId !== id) return;
        if (!urls[i].match(urlRegex)) {
          str = text.replace(
            urls[i],
            `<a href="https://${urls[i]}" target="_blank" rel="noopener noreferrer">${urls[i]}</a>`
          );
        } else {
          str = text.replace(
            urls[i],
            `<a href="${urls[i]}" target="_blank" rel="noopener noreferrer">${urls[i]}</a>`
          );
        }

        this.getMetadata(urls[i]);
      } catch (e) {
        console.error('Error replacing url', e);
      }
    }
    return str;
  }

  getMetadata(url: string) {
    // only preview the first link for now
    if (_.get(this.linkInformation, 'linkUrl')) return;
    this.crudApi
      .post('/api/metadata', { url: url })
      .then(res => {
        this.linkInformation.title = res.data.title || '';
        this.linkInformation.description = res.data.description || '';

        this.linkInformation.image = res.data.image || '';
        this.linkInformation.linkUrl = res.data.linkUrl || '';
        this.linkInformation.ampUrl = res.data.ampUrl || '';
      })
      .catch(e => console.error('error in chain', e));
  }
}



// WEBPACK FOOTER //
// ./src/components/comment/comment.ts